import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Command, Plus, Play, Trash2, Terminal, Activity, Sun, PowerOff, MessageSquare } from "lucide-react";
import { apiGet, protectedPost, protectedDelete } from "@/lib/api";
import { cn } from "@/lib/utils";
const rise = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 } };

const ACTIONS = [
  { id: "status", label: "Report Status", icon: Activity, desc: "JARVIS reports live system health", color: "#82a6ff", payload: false },
  { id: "briefing", label: "Morning Briefing", icon: Sun, desc: "Generate the full daily briefing", color: "#ffb800", payload: false },
  { id: "terminal", label: "Run Terminal", icon: Terminal, desc: "Execute a shell command (terminal must be enabled)", color: "#18c9ff", payload: "command", placeholder: "e.g. docker ps -a" },
  { id: "emergency", label: "Emergency Toggle", icon: PowerOff, desc: "Shut down or restore the VPS fleet", color: "#ef4444", payload: "toggle" },
  { id: "prompt", label: "Send Prompt", icon: MessageSquare, desc: "Send a canned prompt to Jarvis", color: "#b45cff", payload: "text", placeholder: "e.g. Summarise today's revenue and flag risks" },
];
const actionOf = (id) => ACTIONS.find((a) => a.id === id) || ACTIONS[0];

export default function CommandsPage() {
  const [commands, setCommands] = useState([]);
  const [name, setName] = useState("");
  const [phrase, setPhrase] = useState("");
  const [actionType, setActionType] = useState("status");
  const [payload, setPayload] = useState("");
  const [running, setRunning] = useState(null);

  const load = () => apiGet("/commands").then(setCommands).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim() || !phrase.trim()) return toast.error("Name and trigger phrase are required.");
    try {
      await protectedPost("/commands", { name: name.trim(), phrase: phrase.trim(), action_type: actionType, payload });
      toast.success(`Command "${name}" saved, Sir.`);
      setName(""); setPhrase(""); setPayload(""); setActionType("status");
      load();
    } catch (e) {
      toast.error(e.message || "Failed to save command.");
    }
  };

  const run = async (c) => {
    setRunning(c.id);
    try {
      const res = await protectedPost(`/commands/${c.id}/run`);
      const r = res.result;
      const out = r.output || (r.type === "prompt" ? r.prompt : "Executed.");
      toast[r.ok === false ? "error" : "success"](out.length > 160 ? out.slice(0, 160) + "…" : out);
    } catch (e) {
      toast.error(e.message || "Execution failed.");
    }
    setRunning(null);
  };

  const remove = async (c) => {
    try { await protectedDelete(`/commands/${c.id}`); load(); toast.message(`Removed "${c.name}".`); }
    catch (e) { toast.error(e.message || "Delete failed."); }
  };

  const activeAction = actionOf(actionType);

  return (
    <div className="h-full overflow-y-auto p-5 lg:p-6" data-testid="commands-page">
      <div className="flex items-center gap-3 mb-6">
        <Command className="w-6 h-6 text-jarvis-cyan" />
        <div>
          <h1 className="font-mono text-xl lg:text-2xl uppercase tracking-[0.25em] font-bold text-jarvis-cyan jv-glow">Commands</h1>
          <p className="font-mono text-[11px] text-slate-500 tracking-widest">{commands.length} saved · type the trigger phrase in chat to run</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Builder */}
        <div className="jv-panel p-4 lg:col-span-1 h-fit" data-testid="command-builder">
          <div className="flex items-center gap-2 mb-4 font-mono text-[11px] uppercase tracking-widest text-jarvis-cyanBright">
            <Plus className="w-4 h-4" /> New Command
          </div>
          <label className="block font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-1">Name</label>
          <input data-testid="command-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Lockdown"
            className="w-full mb-3 bg-black/40 border border-jarvis-cyan/20 rounded-md px-3 py-2 text-sm font-code text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-jarvis-cyan/50" />

          <label className="block font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-1">Trigger phrase</label>
          <input data-testid="command-phrase-input" value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder="lockdown everything"
            className="w-full mb-3 bg-black/40 border border-jarvis-cyan/20 rounded-md px-3 py-2 text-sm font-code text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-jarvis-cyan/50" />

          <label className="block font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Action</label>
          <div className="grid grid-cols-1 gap-1.5 mb-3">
            {ACTIONS.map((a) => {
              const Icon = a.icon;
              const active = actionType === a.id;
              return (
                <button key={a.id} onClick={() => { setActionType(a.id); setPayload(""); }} data-testid={`command-action-${a.id}`}
                  className={cn("flex items-center gap-2.5 px-3 py-2 rounded-md border text-left transition-all",
                    active ? "border-jarvis-cyan/50 bg-jarvis-cyan/10" : "border-white/10 hover:border-white/20")}>
                  <Icon className="w-4 h-4 shrink-0" style={{ color: a.color }} />
                  <div className="min-w-0">
                    <div className="font-mono text-[11px] text-slate-200">{a.label}</div>
                    <div className="text-[10px] text-slate-500 truncate">{a.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {activeAction.payload === "command" || activeAction.payload === "text" ? (
            <>
              <label className="block font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                {activeAction.payload === "command" ? "Shell command" : "Prompt"}
              </label>
              <textarea data-testid="command-payload-input" value={payload} onChange={(e) => setPayload(e.target.value)} rows={3}
                placeholder={activeAction.placeholder}
                className="w-full mb-3 bg-black/40 border border-jarvis-cyan/20 rounded-md px-3 py-2 text-sm font-code text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-jarvis-cyan/50" />
            </>
          ) : null}
          {actionType === "terminal" && (
            <p className="mb-3 -mt-1 text-[10px] leading-relaxed text-jarvis-red/80 font-mono" data-testid="terminal-macro-warning">
              ⚠ Runs a real shell command inside the container. Only runnable while the Terminal page is enabled.
            </p>
          )}
          {activeAction.payload === "toggle" && (
            <div className="flex gap-2 mb-3">
              {[["on", "Shut down"], ["off", "Restore"]].map(([v, l]) => (
                <button key={v} onClick={() => setPayload(v)} data-testid={`command-toggle-${v}`}
                  className={cn("flex-1 font-mono text-[10px] uppercase tracking-wider px-3 py-2 rounded-md border transition-all",
                    payload === v ? "border-jarvis-red/50 text-jarvis-red bg-jarvis-red/10" : "border-white/10 text-slate-400")}>
                  {l}
                </button>
              ))}
            </div>
          )}

          <button onClick={create} data-testid="command-save-btn"
            className="w-full flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-wider py-2.5 rounded-md bg-jarvis-cyan/15 border border-jarvis-cyan/40 text-jarvis-cyan hover:bg-jarvis-cyan/25 transition-all">
            <Plus className="w-4 h-4" /> Save Command
          </button>
        </div>

        {/* List */}
        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 content-start">
          {commands.length === 0 && (
            <div className="col-span-full jv-panel p-8 text-center" data-testid="commands-empty">
              <Command className="w-8 h-8 text-slate-600 mx-auto mb-3" />
              <p className="font-mono text-sm text-slate-400">No commands yet, Sir.</p>
              <p className="text-[12px] text-slate-600 mt-1">Create one on the left, then type its phrase in the chat dock or press play here.</p>
            </div>
          )}
          {commands.map((c) => {
            const a = actionOf(c.action_type);
            const Icon = a.icon;
            return (
              <motion.div key={c.id} layout {...rise}
                className="jv-panel p-4 flex flex-col" data-testid={`command-card-${c.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-9 h-9 rounded-md flex items-center justify-center border shrink-0"
                      style={{ color: a.color, borderColor: `${a.color}55`, background: `${a.color}12` }}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-mono text-sm font-bold text-slate-100 truncate">{c.name}</div>
                      <span className="font-mono text-[9px] uppercase tracking-wider" style={{ color: a.color }}>{a.label}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => run(c)} disabled={running === c.id} data-testid={`command-run-${c.id}`}
                      className="w-8 h-8 flex items-center justify-center rounded-md border border-jarvis-green/40 text-jarvis-green hover:bg-jarvis-green/15 transition-all disabled:opacity-40">
                      <Play className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => remove(c)} data-testid={`command-delete-${c.id}`}
                      className="w-8 h-8 flex items-center justify-center rounded-md border border-white/10 text-slate-500 hover:text-jarvis-red hover:border-jarvis-red/40 transition-all">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="mt-1 font-code text-[11px] text-slate-400">
                  <span className="text-slate-600">trigger:</span> “{c.phrase}”
                </div>
                {c.payload && <div className="mt-1 font-code text-[11px] text-slate-500 truncate"><span className="text-slate-600">arg:</span> {c.payload}</div>}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
